import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { emitToast } from '../../context/toast';

const ESTADOS = [
  { id: 'activo', label: 'Activo' },
  { id: 'inactivo', label: 'Inactivo' },
  { id: 'pago_pendiente', label: 'Pago pendiente' },
  { id: 'revisado', label: 'Revisado' },
];

const ADJUST_PRESETS = [-30, -15, -7, 7, 15, 30, 60, 90];

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CO');
}

function formatDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
}

function badgeClass(estado) {
  if (estado === 'activo') return 'text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800';
  if (estado === 'revisado') return 'text-xs font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-800';
  if (estado === 'pago_pendiente') return 'text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900';
  return 'text-xs font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-800';
}

function waUrl(phone, name) {
  let dig = String(phone || '').replace(/\D/g, '');
  if (!dig) return null;
  if (dig.length === 10 && dig.startsWith('3')) dig = `57${dig}`;
  const msg = encodeURIComponent(`Hola ${name || ''}, te escribimos sobre tu plan de entrenamiento.`.trim());
  return `https://wa.me/${dig}?text=${msg}`;
}

function ExtendModal({ row, days, setDays, saving, onClose, onSave }) {
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="form-modal d28d-admin-modal ui-light-panel surface-readable training-light-island w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-stone-900 m-0">Ajustar vigencia Training</h2>
        <p className="text-sm text-stone-600 mt-1 mb-3">
          {row.usuario_nombre}
          {' · '}
          {row.plan || 'Sin plan'}
        </p>
        <p className="text-xs text-stone-500 mb-2">
          Suma o resta días solo de Training. No modifica Food ni D28D. No activa pendientes (usa Activar clientes).
        </p>
        <div className="flex flex-wrap gap-1 mb-2">
          {ADJUST_PRESETS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(String(d))}
              className={`text-xs px-2 py-1 rounded-lg border ${
                days === String(d)
                  ? 'bg-stone-900 text-white border-stone-900'
                  : 'border-stone-200 text-stone-700 bg-white'
              }`}
            >
              {d > 0 ? `+${d}` : d}
            </button>
          ))}
        </div>
        <input
          className="input w-full mb-4"
          type="number"
          min={-365}
          max={365}
          value={days}
          onChange={(e) => setDays(e.target.value)}
        />
        <div className="flex gap-2 justify-end">
          <button type="button" className="btn-secondary text-sm" disabled={saving} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={saving || Number(days) === 0}
            onClick={onSave}
          >
            {saving ? 'Guardando…' : `${Number(days) < 0 ? 'Restar' : 'Ampliar'} ${Math.abs(Number(days) || 0)} días`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function TrainingCoachVigenciasPanel({ onBack, onOpenActivateClients }) {
  const [data, setData] = useState({
    pending: [],
    expiring: [],
    licenses: [],
    alerts: [],
    plans: [],
  });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('licenses');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [search, setSearch] = useState('');
  const [extendRow, setExtendRow] = useState(null);
  const [days, setDays] = useState('30');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/coach/vigencias/overview');
      setData({
        pending: r.data?.pending || [],
        expiring: r.data?.expiring || [],
        licenses: r.data?.licenses || [],
        alerts: r.data?.alerts || [],
        plans: r.data?.plans || [],
      });
    } catch (e) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: e.response?.data?.error || 'No se pudieron cargar vigencias Training',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const matchSearch = useCallback((row) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${row.usuario_nombre} ${row.usuario_email} ${row.plan || ''}`.toLowerCase().includes(q);
  }, [search]);

  const filteredLicenses = useMemo(() => (
    (data.licenses || []).filter(matchSearch).filter((r) => !estadoFilter || r.estado === estadoFilter)
  ), [data.licenses, matchSearch, estadoFilter]);

  const filteredPending = useMemo(() => (
    (data.pending || []).filter(matchSearch)
  ), [data.pending, matchSearch]);

  const filteredExpiring = useMemo(() => (
    (data.expiring || [])
      .filter(matchSearch)
      .filter((r) => !estadoFilter || r.estado === estadoFilter)
      .slice()
      .sort((a, b) => (a.days_left ?? 999) - (b.days_left ?? 999))
  ), [data.expiring, matchSearch, estadoFilter]);

  const filteredAlerts = useMemo(() => (
    (data.alerts || []).filter(matchSearch)
  ), [data.alerts, matchSearch]);

  const setEstado = async (row, estado) => {
    if (estado === row.estado) return;
    try {
      await api.put(`/training/coach/vigencias/${row.user_id}/status`, { estado });
      emitToast({ type: 'success', title: 'Estado', message: 'Estado actualizado' });
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: e.response?.data?.error || 'No se pudo cambiar el estado',
      });
    }
  };

  const setPlan = async (row, nextPlan) => {
    if (!nextPlan || nextPlan === row.plan) return;
    if (!window.confirm(`¿Cambiar plan a «${nextPlan}»? Solo afecta Training (no Food ni D28D).`)) return;
    try {
      await api.put(`/training/coach/vigencias/${row.user_id}/plan`, { plan: nextPlan });
      emitToast({ type: 'success', title: 'Plan', message: 'Plan actualizado' });
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: e.response?.data?.error || 'No se pudo cambiar el plan',
      });
    }
  };

  const doExtend = async () => {
    if (!extendRow) return;
    setSaving(true);
    try {
      await api.post(`/training/coach/vigencias/${extendRow.user_id}/extend`, { days: Number(days) });
      emitToast({ type: 'success', title: 'Vigencia', message: 'Días ajustados' });
      setExtendRow(null);
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: e.response?.data?.error || 'No se pudo ajustar la vigencia',
      });
    } finally {
      setSaving(false);
    }
  };

  const openWa = async (row) => {
    const url = waUrl(row.usuario_telefono, row.usuario_nombre);
    if (!url) {
      emitToast({ type: 'warning', title: 'WhatsApp', message: 'Sin teléfono' });
      return;
    }
    try {
      await api.post(`/training/coach/vigencias/${row.user_id}/whatsapp-touch`);
      load();
    } catch {
      /* still open WA */
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const exportCsv = (rows) => {
    if (!rows.length) {
      emitToast({ type: 'warning', title: 'CSV', message: 'No hay filas para exportar' });
      return;
    }
    const header = ['Nombre', 'Email', 'Teléfono', 'Plan', 'Estado', 'Inicio', 'Vence', 'Días', 'WhatsApp'];
    const lines = [header.join(',')];
    rows.forEach((r) => {
      lines.push([
        `"${String(r.usuario_nombre || '').replace(/"/g, '""')}"`,
        r.usuario_email || '',
        r.usuario_telefono || '',
        `"${String(r.plan || '').replace(/"/g, '""')}"`,
        r.estado,
        formatDate(r.start_date),
        formatDate(r.valid_until),
        r.days_left ?? '',
        formatDateTime(r.whatsapp_last_at),
      ].join(','));
    });
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `vigencias_training_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const tabs = [
    { id: 'alerts', label: 'Alertas', count: filteredAlerts.length },
    { id: 'pending', label: 'Pendientes', count: filteredPending.length },
    { id: 'expiring', label: 'Por vencer', count: filteredExpiring.length },
    { id: 'licenses', label: 'Licencias', count: filteredLicenses.length },
  ];

  const csvRows = tab === 'pending'
    ? filteredPending
    : tab === 'expiring'
      ? filteredExpiring
      : tab === 'alerts'
        ? filteredAlerts
        : filteredLicenses;

  const renderTable = (rows, { extend = false, estado = true, pendingCta = false } = {}) => (
    <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            {['Nombre', 'Plan', 'Estado', 'Vence', 'Días', 'WhatsApp', 'Acciones'].map((h) => (
              <th key={h} className="text-left text-xs font-semibold text-stone-500 px-3 py-2">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-3 py-10 text-center text-stone-500">Sin registros.</td>
            </tr>
          ) : rows.map((r) => (
            <tr key={`${r.user_id}-${r.alert || r.estado}`} className="hover:bg-slate-50/80">
              <td className="px-3 py-2">
                <p className="font-medium text-stone-900 m-0">{r.usuario_nombre}</p>
                <p className="text-xs text-stone-500 m-0">{r.usuario_email}</p>
                {r.alert ? (
                  <p className="text-[10px] uppercase font-semibold text-amber-700 m-0 mt-0.5">{r.alert}</p>
                ) : null}
              </td>
              <td className="px-3 py-2">
                <select
                  className="input text-xs py-1 min-w-[140px]"
                  value={r.plan || ''}
                  onChange={(e) => setPlan(r, e.target.value)}
                >
                  {!r.plan && <option value="">Sin plan</option>}
                  {r.plan && !(data.plans || []).some((p) => p.nombre === r.plan) && (
                    <option value={r.plan}>{r.plan}</option>
                  )}
                  {(data.plans || []).map((p) => (
                    <option key={p.nombre} value={p.nombre}>{p.nombre}</option>
                  ))}
                </select>
              </td>
              <td className="px-3 py-2">
                {estado ? (
                  <select
                    className="input text-xs py-1"
                    value={r.estado}
                    onChange={(e) => setEstado(r, e.target.value)}
                  >
                    {(r.is_pending
                      ? ESTADOS.filter((e) => e.id === 'pago_pendiente' || e.id === 'inactivo')
                      : ESTADOS
                    ).map((e) => (
                      <option key={e.id} value={e.id}>{e.label}</option>
                    ))}
                  </select>
                ) : (
                  <span className={badgeClass(r.estado)}>{String(r.estado || '').replace(/_/g, ' ')}</span>
                )}
              </td>
              <td className="px-3 py-2 text-stone-600 whitespace-nowrap">{formatDate(r.valid_until)}</td>
              <td className="px-3 py-2">
                <span className={`text-xs font-medium ${
                  (r.days_left ?? 99) <= 5
                    ? 'text-rose-600'
                    : (r.days_left ?? 99) <= 15
                      ? 'text-amber-700'
                      : 'text-emerald-700'
                }`}
                >
                  {r.days_left == null ? '—' : `${r.days_left}d`}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-stone-600 whitespace-nowrap">
                {formatDateTime(r.whatsapp_last_at)}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {pendingCta && onOpenActivateClients ? (
                    <button
                      type="button"
                      className="btn btn-primary text-xs py-1 px-2"
                      onClick={onOpenActivateClients}
                    >
                      Activar clientes
                    </button>
                  ) : null}
                  {extend && (r.estado === 'activo' || r.estado === 'revisado' || r.estado === 'inactivo') ? (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs py-1 px-2"
                      onClick={() => { setDays('30'); setExtendRow(r); }}
                    >
                      Ajustar días
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-secondary text-xs py-1 px-2"
                    title="WhatsApp"
                    onClick={() => openWa(r)}
                  >
                    WA
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="dashboard-main-view training-coach-view training-light-island surface-readable d28d-admin-surface max-w-6xl mx-auto p-5 space-y-4">
      <div className="card ui-light-panel p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-stone-900 m-0">Vigencias Training</h2>
            <p className="text-sm text-stone-600 mt-1 mb-0">
              Solo tus clientes. Estado, días y plan Training. La activación de pendientes va por{' '}
              <strong>Activar clientes</strong>
              {' '}(Wompi).
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <button type="button" className="btn btn-secondary text-sm px-3 py-1.5" onClick={() => exportCsv(csvRows)}>
              CSV
            </button>
            <button type="button" className="btn btn-secondary text-sm px-3 py-1.5" onClick={load} disabled={loading}>
              Actualizar
            </button>
            {onOpenActivateClients ? (
              <button type="button" className="btn btn-primary text-sm px-3 py-1.5" onClick={onOpenActivateClients}>
                Activar clientes
              </button>
            ) : null}
            {onBack ? (
              <button type="button" className="btn btn-secondary text-sm px-3 py-1.5" onClick={onBack}>
                Volver
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <input
            className="input flex-1 min-w-[180px] text-sm"
            placeholder="Buscar nombre o correo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {(tab === 'licenses' || tab === 'expiring') ? (
            <select
              className="input text-sm max-w-[180px]"
              value={estadoFilter}
              onChange={(e) => setEstadoFilter(e.target.value)}
            >
              <option value="">Estado</option>
              {ESTADOS.map((e) => (
                <option key={e.id} value={e.id}>{e.label}</option>
              ))}
            </select>
          ) : null}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-0.5">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`btn text-sm font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap ${
                tab === t.id
                  ? 'btn-primary'
                  : 'btn-secondary'
              }`}
            >
              {t.label}
              {' '}
              (
              {t.count}
              )
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-stone-600 py-10 text-center">Cargando vigencias…</p>
        ) : tab === 'alerts' ? (
          renderTable(filteredAlerts, { extend: true, estado: true })
        ) : tab === 'pending' ? (
          <>
            <p className="text-xs text-stone-600 m-0">
              Pendientes de pago/activación: ábrelos en Activar clientes (Wompi). Aquí no hay «Confirmar» a mano.
            </p>
            {renderTable(filteredPending, { extend: false, estado: true, pendingCta: true })}
          </>
        ) : tab === 'expiring' ? (
          renderTable(filteredExpiring, { extend: true, estado: true })
        ) : (
          renderTable(filteredLicenses, { extend: true, estado: true })
        )}
      </div>

      {extendRow ? (
        <ExtendModal
          row={extendRow}
          days={days}
          setDays={setDays}
          saving={saving}
          onClose={() => !saving && setExtendRow(null)}
          onSave={doExtend}
        />
      ) : null}
    </div>
  );
}
